import "./sandboxStore.css";
import type { ProductId, Store, StoreTransaction } from "@highmarch/app";
import { bindSeg, btnHtml, closeBtnHtml, ribbonHtml, segHtml } from "./kit";

/**
 * More → Sandbox store (debug builds only; FINISH_PLAN D9): drives the web build's sandbox store through every path
 * StoreKit can take — bought, cancelled, pending (then approved or declined), failed, interrupted before the grant —
 * plus refunds, restore and a device with purchases switched off, so the Shop's grant code (WP1/WP5) can be tested
 * on the phone before the iOS app exists. Nothing here is ever charged.
 */
const OUTCOME_LABEL: Readonly<Record<string, string>> = { purchase: "Buy", cancel: "Cancel", pending: "Pending", fail: "Fail", interrupt: "Crash" };
/** Transactions listed: the newest; the ledger keeps more. */
const LISTED_TRANSACTIONS = 8;
/** Log lines kept on the panel. */
const LOG_LINES = 30;

/** The sandbox store's debug controls on top of the app's `Store` port (infra/store/sandboxStore.ts implements them). */
export interface SandboxControls<O extends string> extends Store {
  readonly outcome: O;
  readonly paymentsAllowed: boolean;
  readonly pendingProducts: readonly ProductId[];
  readonly transactions: ReadonlyArray<{ readonly tx: StoreTransaction; readonly finished: boolean }>;
  setOutcome(o: O): void;
  setCanPay(allowed: boolean): void;
  approvePending(): StoreTransaction | null;
  declinePending(): void;
  revoke(transactionId: string): StoreTransaction | null;
  reset(): void;
  onChange(listener: () => void): () => void;
}

/**
 * What the panel drives, handed over by features/platform.ts so Presentation never imports Infrastructure: the store,
 * the outcomes its next purchase can take, and the gem packs on sale.
 */
export interface SandboxPanel<O extends string> {
  readonly store: SandboxControls<O>;
  readonly outcomes: readonly O[];
  readonly listings: ReadonlyArray<{ readonly id: ProductId; readonly displayName: string; readonly referencePriceUsd: string }>;
}

export function showSandboxStore<O extends string>(parent: HTMLElement, panel: SandboxPanel<O>): HTMLElement {
  const { store, outcomes, listings } = panel;
  const sheet = document.createElement("div");
  sheet.className = "sheet panel sandboxstore";
  const log: string[] = [];
  const say = (line: string) => { log.unshift(`${new Date().toISOString().slice(11, 19)} ${line}`); log.length = Math.min(log.length, LOG_LINES); render(); };
  const txLine = (t: StoreTransaction) => `${t.transactionId} · ${t.productId}${t.revokedAtMs !== undefined ? " · REFUNDED" : ""}`;

  const render = () => {
    const txs = store.transactions.slice(0, LISTED_TRANSACTIONS);
    sheet.innerHTML = `${ribbonHtml("SANDBOX STORE", "blue", "stitle")}${closeBtnHtml()}
      <div class="sbody">
        <p class="note">Test store for the web build: nothing is ever charged. Gems arrive only where the Shop grants them.</p>
        <b>Next purchase</b>${segHtml(outcomes.map((o) => ({ key: o, label: OUTCOME_LABEL[o] ?? o })), store.outcome)}
        ${listings.map((p) => `<div class="line"><span>${p.displayName} · ${p.id} · $${p.referencePriceUsd}</span>${btnHtml("BUY", "gold", { className: "buy", data: `data-id="${p.id}"` })}</div>`).join("")}
        <div class="acts">
          ${btnHtml(`APPROVE (${store.pendingProducts.length})`, "blue", { className: "approve", disabled: store.pendingProducts.length === 0 })}
          ${btnHtml("DECLINE", "dark", { className: "decline", disabled: store.pendingProducts.length === 0 })}
          ${btnHtml("UNFINISHED", "dark", { className: "unfinished" })}
          ${btnHtml("RESTORE", "dark", { className: "restore" })}
          ${btnHtml(`PAYMENTS ${store.paymentsAllowed ? "ON" : "OFF"}`, "dark", { className: "canpay" })}
          ${btnHtml("RESET", "danger", { className: "reset" })}
        </div>
        <b>Transactions</b>
        ${txs.length === 0 ? `<p class="note">None yet.</p>` : txs.map(({ tx, finished }) => `<div class="line"><span>${txLine(tx)} · ${finished ? "finished" : "unfinished"}</span>${finished ? "" : btnHtml("FINISH", "dark", { className: "finish", data: `data-id="${tx.transactionId}"` })}${tx.revokedAtMs === undefined ? btnHtml("REFUND", "danger", { className: "refund", data: `data-id="${tx.transactionId}"` }) : ""}</div>`).join("")}
        <div class="log">${log.join("\n") || "Events appear here."}</div>
      </div>`;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => { unsubscribe(); sheet.remove(); };
    bindSeg(sheet, (k) => { const o = outcomes.find((x) => x === k); if (o !== undefined) store.setOutcome(o); });
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".buy")) b.onclick = async () => {
      const id = b.dataset.id ?? "";
      const r = await store.purchase(id);
      say(`purchase ${id}: ${r.status}${r.status === "purchased" ? ` ${r.transaction.transactionId}` : r.status === "failed" ? ` (${r.reason})` : ""}`);
    };
    sheet.querySelector<HTMLButtonElement>(".approve")!.onclick = () => { const t = store.approvePending(); if (t) say(`approved: ${txLine(t)}`); };
    sheet.querySelector<HTMLButtonElement>(".decline")!.onclick = () => { store.declinePending(); say("declined the oldest pending purchase"); };
    sheet.querySelector<HTMLButtonElement>(".unfinished")!.onclick = async () => { const u = await store.unfinished(); say(`unfinished: ${u.length === 0 ? "none" : u.map(txLine).join(", ")}`); };
    sheet.querySelector<HTMLButtonElement>(".restore")!.onclick = async () => { const r = await store.restore(); say(`restore: ${r.length === 0 ? "no entitlements (every pack is consumable)" : r.map(txLine).join(", ")}`); };
    sheet.querySelector<HTMLButtonElement>(".canpay")!.onclick = async () => { store.setCanPay(!store.paymentsAllowed); say(`payments ${store.paymentsAllowed ? "on" : "off (Screen Time)"}`); };
    sheet.querySelector<HTMLButtonElement>(".reset")!.onclick = () => { store.reset(); say("sandbox ledger wiped"); };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".finish")) b.onclick = async () => { await store.finish(b.dataset.id ?? ""); say(`finished ${b.dataset.id}`); };
    for (const b of sheet.querySelectorAll<HTMLButtonElement>(".refund")) b.onclick = () => { const t = store.revoke(b.dataset.id ?? ""); if (t) say(`refunded: ${txLine(t)}`); };
  };
  const offTx = store.onTransaction((t) => say(`onTransaction: ${txLine(t)}`));
  const offChange = store.onChange(() => render());
  const unsubscribe = () => { offTx(); offChange(); };
  render();
  parent.append(sheet);
  return sheet;
}
